import { type ISocialApplyOffer, type TSocialOfferSpotlight, formatName, getDateFormatOptions } from "@api";
import type { ICandidate } from "@bbj-types/candidate";
import type { ISocialOffer } from "@bbj-types/offer";
import type { ISocialApplyTransition } from "@bbj-types/socialApply";
import type { SocialApplyStatus } from "@bbj-types/socialApply/enum";
import type { ObjectId } from "@bbj-types/utils";
import { FollowUpSocialOfferV2 } from "@components/socialOffer/FollowUpSocialOfferV2";
import { IconsGroup } from "@data-display/IconsGroup";
import CloseIcon from "@mui/icons-material/Close";
import { Button, IconButton, Stack, Typography } from "@mui/material";
import { useGlobalContext } from "@root/context";
import { isInternalOfferSource } from "@root/utils/enums/offer";
import { useI18nContext } from "@root/utils/organisation";
import { Authorizer, TypoTooltip, useAuthorizer, useDisplayFupSimple } from "@styleguide";
import { type Dispatch, type SetStateAction, useMemo } from "react";
import type { IStandardDrawerSocialOfferProps } from "../variants/StandardDrawerSocialOffer";
import { ConfirmationAddSocialOfferSpotlightModal } from "./ConfirmationAddSocialOfferSpotlightModal";
import { DrawerSocialOfferActions } from "./icons";
import { DrawerHeader, classes } from "./styles";

export interface IDrawerSocialOfferHeaderProps<
  T extends ISocialOffer & {
    spotlightDates?: { startDate: Date; endDate: Date };
    spotlightId?: ObjectId;
    status?: SocialApplyStatus;
    transitions?: ISocialApplyTransition[];
    socialApplyId?: string;
  },
> {
  offer: IStandardDrawerSocialOfferProps<T>["offer"];
  onClose: IStandardDrawerSocialOfferProps<T>["onClose"];
  handleRemoveOffer?: IStandardDrawerSocialOfferProps<T>["handleRemoveOffer"];
  handleRemoveSocialOfferSpotlight?: IStandardDrawerSocialOfferProps<T>["handleRemoveSocialOfferSpotlight"];
  onUpdateOffer?: IStandardDrawerSocialOfferProps<T>["onUpdateOffer"];
  onUpdateSocialApply?: (status: SocialApplyStatus) => void;
  openEditModal?: IStandardDrawerSocialOfferProps<T>["openEditModal"];
  candidate?: ICandidate;
  expired: boolean;
  isApply?: boolean;
  setSocialApplyOfferFupModal?: Dispatch<SetStateAction<ISocialApplyOffer | null>>;
}

const isSocialOfferSpotlight = <
  T extends ISocialOffer & { spotlightDates?: { startDate: Date; endDate: Date }; spotlightId?: ObjectId },
>(
  value: T | TSocialOfferSpotlight
): value is TSocialOfferSpotlight => {
  return (
    (value as TSocialOfferSpotlight).spotlightDates !== undefined &&
    (value as TSocialOfferSpotlight).spotlightId !== undefined
  );
};

export const DrawerSocialOfferHeader = <
  T extends ISocialOffer & {
    spotlightDates?: { startDate: Date; endDate: Date };
    spotlightId?: ObjectId;
    status?: SocialApplyStatus;
    transitions?: ISocialApplyTransition[];
    socialApplyId?: string;
  },
>({
  offer,
  onClose,
  handleRemoveOffer,
  handleRemoveSocialOfferSpotlight,
  onUpdateOffer,
  onUpdateSocialApply,
  openEditModal,
  candidate,
  expired,
  isApply,
  setSocialApplyOfferFupModal,
}: IDrawerSocialOfferHeaderProps<T>) => {
  const manualOffer = isInternalOfferSource(offer.source);
  const { settingsV2 } = useGlobalContext();
  const { timeZone } = useI18nContext();
  const { icons, limit, addSpotlightModal, setAddSpotlightModal } = DrawerSocialOfferActions(
    offer,
    onClose,
    expired,
    handleRemoveOffer,
    handleRemoveSocialOfferSpotlight,
    onUpdateOffer,
    openEditModal,
    isApply
  );
  const canDeleteSpotlight = useAuthorizer(["offers.social.spotlight"]);
  const displayFup = useDisplayFupSimple(offer);

  const [socialOfferSpotlightStartDate, socialOfferSpotlightEndDate] = useMemo(() => {
    if (isSocialOfferSpotlight(offer)) {
      const startDate = offer.spotlightDates.startDate.toLocaleString("fr-FR", getDateFormatOptions(timeZone));

      const endDate = offer.spotlightDates.endDate.toLocaleString("fr-FR", getDateFormatOptions(timeZone));
      return [startDate, endDate];
    }
    return [];
  }, [offer]);

  const creatorEditorText = useMemo(() => {
    if (offer.editor && offer.publicationUpdatedDate) {
      return `Modifiée par ${formatName(offer.editor)} le ${offer.publicationUpdatedDate.toLocaleString("fr-FR", getDateFormatOptions(timeZone))}.`;
    }
    if (offer.creator && offer.publicationDate) {
      return `Créée par ${formatName(offer.creator)} le ${offer.publicationDate.toLocaleString("fr-FR", getDateFormatOptions(timeZone))}.`;
    }
    return "Créée manuellement.";
  }, [offer.editor, offer.creator, offer.publicationUpdatedDate, offer.publicationDate]);
  return (
    <>
      <DrawerHeader className={classes.root}>
        <Stack className={classes.topContainer}>
          <Stack className={classes.titleContainer}>
            <TypoTooltip className={classes.title} variant="h2" color={expired ? "grayText" : "primary"}>
              {expired && "[Expirée] - "}
              {offer.name} {offer.provider?.name && `- ${offer.provider?.name}`}
            </TypoTooltip>
            <Typography
              variant="body1"
              sx={{
                color: "black.light",
              }}
            >
              {manualOffer
                ? creatorEditorText
                : `Par ${settingsV2.settingsSourceSocialOffer?.values.find((el) => el.value === offer.source)?.label ?? offer.source}.`}
              {canDeleteSpotlight &&
                isSocialOfferSpotlight(offer) &&
                ` À la une du ${socialOfferSpotlightStartDate} au ${socialOfferSpotlightEndDate}.`}
            </Typography>
          </Stack>
          <Stack direction="row">
            <IconsGroup icons={icons} limit={limit} />
            <IconButton id="drawer-social-offer-close-button" onClick={onClose}>
              <CloseIcon color="primary" fontSize="small" />
            </IconButton>
          </Stack>
        </Stack>
        <Stack
          direction="row"
          sx={{
            gap: 2,
            alignItems: "center",
          }}
        >
          <Authorizer requiredPermissions={["candidate.socialboard.apply.modify"]}>
            {candidate && (
              <FollowUpSocialOfferV2
                offer={offer}
                candidate={candidate}
                onUpdateOffer={
                  onUpdateOffer
                    ? (updatedOffer) => {
                        onClose();
                        onUpdateOffer(
                          updatedOffer as T & {
                            status?: SocialApplyStatus;
                            transitions?: ISocialApplyTransition[];
                            socialApplyId?: string;
                          }
                        );
                      }
                    : undefined
                }
                expired={expired}
                displayFup={displayFup}
                setOfferFupModal={setSocialApplyOfferFupModal}
              />
            )}
          </Authorizer>
          {offer.provider?.detailsAccess && (
            <Authorizer requiredPermissions={["offers.social.visualize"]}>
              <Button
                component="a"
                href={`/offer/social/${offer.offerId}`}
                target="_blank"
                rel="noopener"
                color="primary"
                variant="outlined"
                size="small"
                sx={{ backgroundColor: "white" }}
              >
                Voir l'offre sur horizon
              </Button>
            </Authorizer>
          )}
        </Stack>
      </DrawerHeader>
      {addSpotlightModal && (
        <ConfirmationAddSocialOfferSpotlightModal
          openModal={addSpotlightModal}
          setOpenModal={setAddSpotlightModal}
          offer={offer}
          onUpdateOffer={onUpdateOffer}
          onClose={onClose}
        />
      )}
    </>
  );
};
